import type { ReactNode } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";
import { BooleanSwitchValue } from "consts/general";
import { getFormInputProps } from "util/forms";
import { OnboardingForm, OnboardingReviewableFormInput } from "ui/forms/Onboarding/shared";
import { FormField, type OnboardingMediaAndComplianceCheckProps } from "./types";

const MediaAndComplianceCheck = ({
    values,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingMediaAndComplianceCheckProps) => {
    const rootSx = {
        gridTemplateColumns: '1fr auto auto',
        gap: 2,
        alignItems: 'flex-start'
    };

    const renderReviewableStatus = (reviewableStatus: ReactNode) => (
        <Box
            sx={{
                mt: 1
            }}
        >
            {reviewableStatus}
        </Box>
    );

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
            >
                Has the company or its leadership been subject to adverse media coverage?
            </Typography>
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.HasAdverseMediaCoverage}
                values={values}
                renderLabel={() => 'Adverse Media Coverage'}
                renderFormInput={({ disabled, reviewableStatus }) => (
                    <>
                        <RadioGroup
                            aria-labelledby={FormField.HasAdverseMediaCoverage}
                            name={FormField.HasAdverseMediaCoverage}
                            value={values[FormField.HasAdverseMediaCoverage]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.On}
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.Off}
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.AdverseMediaCoverageInput}
                values={values}
                renderLabel={() => 'Adverse Media Coverage'}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <>
                        <TextField
                            {...getFormInputProps(FormField.AdverseMediaCoverageInput, form)}
                            required
                            multiline
                            fullWidth
                            rows={4}
                            size='small'
                            inputRef={inputRef}
                            disabled={disabled}
                            name={FormField.AdverseMediaCoverageInput}
                            value={values[FormField.AdverseMediaCoverageInput]}
                            label='IF YES, please provide details and sources'
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <Typography
                variant='h6'
                sx={{
                    mt: 2
                }}
            >
                Is the company or its leadership subject to legal investigations or enforcement actions?
            </Typography>
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.HasLegalInvestigation}
                values={values}
                renderLabel={() => 'Legal Investigations'}
                renderFormInput={({ disabled, reviewableStatus }) => (
                    <>
                        <RadioGroup
                            aria-labelledby={FormField.HasLegalInvestigation}
                            name={FormField.HasLegalInvestigation}
                            value={values[FormField.HasLegalInvestigation]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.On}
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.Off}
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.LegalInvestigationInput}
                values={values}
                renderLabel={() => 'Legal Investigations'}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <>
                        <TextField
                            {...getFormInputProps(FormField.LegalInvestigationInput, form)}
                            required
                            multiline
                            fullWidth
                            rows={4}
                            size='small'
                            inputRef={inputRef}
                            disabled={disabled}
                            name={FormField.LegalInvestigationInput}
                            value={values[FormField.LegalInvestigationInput]}
                            label='IF YES, please provide details'
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <Typography
                variant='h6'
                sx={{
                    mt: 2
                }}
            >
                Does your business have certifications? (ISO, PCI DSS, etc.)
            </Typography>
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.HasCertification}
                values={values}
                renderLabel={() => 'Business Certifications'}
                renderFormInput={({ disabled, reviewableStatus }) => (
                    <>
                        <RadioGroup
                            aria-labelledby={FormField.HasCertification}
                            name={FormField.HasCertification}
                            value={values[FormField.HasCertification]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.On}
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.Off}
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <Typography
                variant='h6'
                sx={{
                    mt: 2
                }}
            >
                Are you compliant with local tax regulations?
            </Typography>
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.IsCompliantWithTax}
                values={values}
                renderLabel={() => 'Tax Compliance'}
                renderFormInput={({ disabled, reviewableStatus }) => (
                    <>
                        <RadioGroup
                            aria-labelledby={FormField.IsCompliantWithTax}
                            name={FormField.IsCompliantWithTax}
                            value={values[FormField.IsCompliantWithTax]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.On}
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.Off}
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <Typography
                variant='h6'
                sx={{
                    mt: 2
                }}
            >
                Are you registered with any financial or regulatory authority?
            </Typography>
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.IsRegisteredWithAuthority}
                values={values}
                renderLabel={() => 'Regulatory Authority Registration'}
                renderFormInput={({ disabled, reviewableStatus }) => (
                    <>
                        <RadioGroup
                            aria-labelledby={FormField.IsRegisteredWithAuthority}
                            name={FormField.IsRegisteredWithAuthority}
                            value={values[FormField.IsRegisteredWithAuthority]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.On}
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                disabled={disabled}
                                value={BooleanSwitchValue.Off}
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
            <OnboardingReviewableFormInput
                {...form}
                sx={rootSx}
                name={FormField.RegisteredWithAuthorityInput}
                values={values}
                renderLabel={() => 'Regulatory Authority Registration'}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <>
                        <TextField
                            {...getFormInputProps(FormField.RegisteredWithAuthorityInput, form)}
                            required
                            multiline
                            fullWidth
                            rows={4}
                            size='small'
                            inputRef={inputRef}
                            disabled={disabled}
                            name={FormField.RegisteredWithAuthorityInput}
                            value={values[FormField.RegisteredWithAuthorityInput]}
                            label='IF YES, please provide details'
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {renderReviewableStatus(reviewableStatus)}
                    </>
                )}
            />
        </OnboardingForm>
    );
};

export default MediaAndComplianceCheck;
